import { useEffect, useState } from "react";

export interface IModalDimensions {
  modalTop: string;
  maxHeight: string;
}

/* eslint-disable import/no-anonymous-default-export */
export default (modalRef: React.RefObject<HTMLDivElement>): IModalDimensions => {
  const [modalTop, setModalTop] = useState("50vh");
  const [maxHeight, setMaxHeight] = useState("90vh");
  const VERTICAL_MARGIN = 5; // Marża od krawędzi viewport'u w px

  // Nasłuchujemy komunikatów z rodzica
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data === "string" && event.data.startsWith("parentScrollInfo::")) {
        const parts = event.data.split("::");
        if (parts.length >= 5) {
          const parentScrollValue = parseFloat(parts[1]);
          const viewportHeight = parseFloat(parts[2]);
          const iframeTopPosition = parseFloat(parts[3]);
          const iframeBottomPosition = parseFloat(parts[4]);
          if (
            !isNaN(parentScrollValue) &&
            !isNaN(viewportHeight) &&
            !isNaN(iframeTopPosition) &&
            !isNaN(iframeBottomPosition)
          ) {
            updateModalPosition(parentScrollValue, viewportHeight, iframeTopPosition, iframeBottomPosition);
          }
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
    //eslint-disable-next-line
  }, []);

  const updateModalPosition = (
    parentScroll: number,
    viewportHeight: number,
    iframeTopPosition: number,
    iframeBottomPosition: number
  ) => {
    if (window.self !== window.top) {
      // Jesteśmy w iframe, używamy pozycji iframe
      if (modalRef && modalRef.current) {
        const modalHeight = modalRef.current.offsetHeight;

        // Zabezpieczenie - czekaj aż modal się wyrenderuje
        if (modalHeight === 0) {
          return;
        }

        // Naturalny środek modala względem rodzica, przeliczony na współrzędne iframe
        let candidate = parentScroll + viewportHeight / 2 - iframeTopPosition;

        const iframeHeight = iframeBottomPosition - iframeTopPosition;
        // Dolna granica – aby górna część modala nie wyszła poza iframe
        const lowerBound = modalHeight / 2;
        // Górna granica – aby dolna część modala była widoczna
        const upperBound = iframeHeight - modalHeight / 2;
        // Klamrujemy candidate do zakresu [lowerBound, upperBound]
        candidate = Math.min(Math.max(candidate, lowerBound), upperBound);

        // Obliczenie gdzie modal będzie względem viewport'u
        const modalTopInViewport = candidate - modalHeight / 2 + iframeTopPosition - parentScroll;
        const modalBottomInViewport = modalTopInViewport + modalHeight;

        // Dostępna wysokość: całość viewport'u minus marginesy
        let availableHeight = viewportHeight - 2 * VERTICAL_MARGIN;

        // Jeśli modal wychodzi poza górę viewport'u
        if (modalTopInViewport < VERTICAL_MARGIN) {
          const overlap = VERTICAL_MARGIN - modalTopInViewport;
          availableHeight -= overlap;
        }

        // Jeśli modal wychodzi poza dół viewport'u
        if (modalBottomInViewport > viewportHeight - VERTICAL_MARGIN) {
          const overlap = modalBottomInViewport - (viewportHeight - VERTICAL_MARGIN);
          availableHeight -= overlap;
        }

        const calculatedMaxHeight = availableHeight;

        setModalTop(`${candidate}px`);
        setMaxHeight(`${Math.max(calculatedMaxHeight, 200)}px`); // Min 200px aby nie był za mały
      } else {
        const correctedTop = parentScroll + viewportHeight / 2 - iframeTopPosition;
        setModalTop(`${correctedTop}px`);
        setMaxHeight("90vh");
      }
    } else {
      // Nie jesteśmy w iframe
      setModalTop("50vh");
      setMaxHeight("90vh");
    }
  };

  return { modalTop, maxHeight };
};